import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { IPermission, ITransferListItem } from "@miplatform/types";
import { TransferList } from "@mibuildweb/components";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IRoleFormData } from "../../ConfigRoles.types";
import { getPermissionNameType } from "../utils/getModulesByAccessPermissions";

export const RoleWorkPermissionsSubTabView = ({
  form,
  extraTabProps,
}: {
  form: UseFormReturn<IRoleFormData>;
  extraTabProps?: {
    permissionsList?: IPermission[];
    availablePermissions?: IPermission[];
    selectedPermissions?: IPermission[];
    isReadOnly?: boolean;
  };
}) => {
  const { getValues, setValue } = form;
  const { isReadOnly, availablePermissions, selectedPermissions } =
    extraTabProps || {};

  const addPermission = (permissionName: string) => {
    const currentPermissions = getValues().workPermissionNames || [];

    if (currentPermissions.includes(permissionName)) {
      return;
    }

    setValue("workPermissionNames", [...currentPermissions, permissionName], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const removePermission = (permissionName: string) => {
    const currentPermissions = getValues().workPermissionNames || [];
    setValue(
      "workPermissionNames",
      currentPermissions.filter((name: string) => name !== permissionName),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const getGroupByInfo = (groupKey: string) => {
    const group = permissionGroupsDictionary[groupKey] || {
      key: groupKey,
      name: "No Group Found",
    };
    return {
      name: getPermissionNameType(group.name),
      description: group?.description,
      key: group.key,
    };
  };

  const availablePermissionsListItems: ITransferListItem[] | undefined =
    availablePermissions?.map((permission) => ({
      key: permission?.customName ?? "",
      title: "",
      ...permission,
    })) ?? [];

  const selectedPermissionsListItems: ITransferListItem[] =
    selectedPermissions?.map((permission) => {
      return {
        key: permission?.customName ?? "",
        title: "",
        ...permission,
      };
    }) ?? [];

  const permissionGroupsDictionary: IPermissionGroupsDictionary =
    useMemo(() => {
      const dict: Record<
        string,
        {
          key: string;
          name: string;
          permissions: IPermission[];
          description?: string;
        }
      > = {};
      extraTabProps?.permissionsList?.forEach((permission) => {
        const entityGroup = permission.entityKey || "Uncategorized";
        if (!dict[entityGroup]) {
          dict[entityGroup] = {
            key: entityGroup,
            name: permission.entityName,
            description: permission.entityDescription,
            permissions: [],
          };
        }
        dict[entityGroup].permissions.push(permission);
      });
      return dict;
    }, [extraTabProps?.permissionsList]);

  return (
    <ConfigAccordionHeader title="Order Permissions">
      <TransferList
        availableTitle="Available Permissions"
        availableItems={availablePermissionsListItems}
        selectedTitle={`Selected Permissions (${selectedPermissionsListItems?.length})`}
        selectedItems={selectedPermissionsListItems}
        onAddItem={
          isReadOnly ? null : (permissionName) => addPermission(permissionName)
        }
        onRemoveItem={isReadOnly ? null : removePermission}
        groupBy="entityKey"
        getGroupByInfo={getGroupByInfo}
      />
    </ConfigAccordionHeader>
  );
};

interface IPermissionGroupsDictionary {
  [key: string]: {
    key: string;
    name: string;
    permissions: IPermission[];
    description?: string;
  };
}
